import React, { useEffect, useMemo, useState } from "react";
import "./MastercardEstimatedProgramStatusTable.less";
import Table, { itemRender } from "../../../../../../Components/Table";
import { getColumns } from "./tableColumns";
import { useTranslation } from "react-i18next";
import { Flex, Pagination, Typography } from "antd";
import cls from "classnames";
import { useDispatch, useSelector } from "react-redux";
import HealthActions from "../../../../reducer";
import SelectComponent from "../../../../../../Components/Select";
import { useDemoDataToggle } from "../../../../../User/hooks/useDemoDataToggle";
import { useIsStripeConnected } from "../../../hooks/useIsStripeConnected";
import { withMonthRowFlags } from "../../../../components/Monitoring/monthRows";
import { DEFAULT_EPS_ORDERING, toOrdering } from "../../../../components/Monitoring/epsSorting";
import EpsNoResults from "../../../../components/Monitoring/EpsNoResults/EpsNoResults";
import { toEpsFilterParams } from "../../../../components/Monitoring/epsFilters";
import { getDates } from "../../../../../../Utils/utils";

const { Text } = Typography;

const METRIC_OPTIONS = [
  { label: "Fraud count", value: "fraud_count" },
  { label: "Dispute count", value: "dispute_count" },
  { label: "Transaction count", value: "transaction_count" },
  { label: "Transaction volume", value: "transaction_volume" },
];

// Purely decorative — gives the Connect Stripe overlay a row to sit on top of
// without fetching real data for an account that isn't connected.
const PLACEHOLDER_ROW = {
  unique_id: "placeholder",
  billing_descriptor: "YOUR BILLING DESCRIPTOR",
  fraud_count: 0,
  dispute_count: 0,
  transaction_count: 0,
  transaction_volume: 0,
};

const MastercardEstimatedProgramStatusTable = ({
  business_account_id,
  variant,
  pageSize,
  withPagination,
  search = "",
  columnFilters,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const {
    mcEstimatedProgramStatus,
    mcEstimatedProgramStatusFetching,
    filters: pageFilters,
    mcMainChart,
  } = useSelector((state) => state.health);
  const limits = useMemo(
    () => ({
      efm: Number(mcMainChart?.thresholds?.efm?.rate) || 0,
      ecm: Number(mcMainChart?.thresholds?.ecm?.rate) || 0,
    }),
    [mcMainChart],
  );
  const isPreview = variant === "block";
  const [ordering, setOrdering] = useState(DEFAULT_EPS_ORDERING);

  const filterParams = useMemo(
    () => (isPreview ? {} : toEpsFilterParams(columnFilters)),
    [isPreview, columnFilters]
  );
  const queryKey = JSON.stringify([search, ordering, pageFilters?.period, filterParams]);
  const [lastQueryKey, setLastQueryKey] = useState(queryKey);
  if (queryKey !== lastQueryKey) {
    setLastQueryKey(queryKey);
    setPageNumber(1);
  }
  const { isMobile } = useSelector((state) => state.nav);
  const { isShowDemoData } = useDemoDataToggle();
  const isStripeConnected = useIsStripeConnected();
  const [selectedMetric, setSelectedMetric] = useState("fraud_count");

  const { results, count } = mcEstimatedProgramStatus ?? {};

  const data = useMemo(() => {
    return results?.map((item) => ({
      ...item,
      months: Object.values(item?.data || {}),
      data: null,
    }));
  }, [results]);

  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    setExpandedKeys([]);
  }, [results]);

  useEffect(() => {
    if (isStripeConnected) {
      dispatch(
        HealthActions.getMcEstimatedProgramStatusRequest({
          business_account: business_account_id,
          page_size: pageSize,
          page: withPagination ? pageNumber : 1,
          ...getDates(pageFilters?.period),
          ...(isPreview ? { ordering: DEFAULT_EPS_ORDERING } : {}),
          ...(!isPreview && ordering ? { ordering } : {}),
          ...(!isPreview && search.trim() ? { search: search.trim() } : {}),
          ...filterParams,
        })
      );
    }
  }, [
    business_account_id,
    pageNumber,
    pageSize,
    withPagination,
    isShowDemoData,
    isStripeConnected,
    pageFilters?.period,
    isPreview,
    ordering,
    search,
    filterParams,
  ]);

  const dataSource = useMemo(() => {
    if (!isStripeConnected) return [PLACEHOLDER_ROW];
    if (!results) return [];

    const mainRows = data?.reduce((acc, item) => {
      const descriptor_row = {
        ...item,
        unique_id: item?.billing_descriptor,
      };
      if (expandedKeys.includes(descriptor_row.billing_descriptor)) {
        const months = withMonthRowFlags(descriptor_row?.months).map((month) => ({
          ...month,
          row_type: "month",
          unique_id: `${descriptor_row?.unique_id}-${month?.month_name}`,
        }));
        return [...acc, descriptor_row, ...months];
      }
      return [...acc, descriptor_row];
    }, []);

    return [...mainRows];
  }, [results, expandedKeys, isStripeConnected]);

  const tableSettings = useMemo(() => ({ scroll: { x: "auto" } }), []);

  const columns = useMemo(
    () =>
      getColumns({
        t,
        expandedKeys,
        isMobile,
        selectedMetric,
        limits,
        sortable: !isPreview && isStripeConnected,
        search,
      }),
    [t, expandedKeys, isMobile, selectedMetric, limits, isPreview, isStripeConnected, search],
  );

  const rowClickHandler = (record) => {
    if (record.row_type === "month") return;
    if (!record?.months?.length) return;
    const key = record.unique_id;
    setExpandedKeys((prevKeys) =>
      prevKeys.includes(key)
        ? prevKeys.filter((k) => k !== key)
        : [...prevKeys, key]
    );
  };

  return (
    <div>
      {isMobile && (
        <Flex
          gap={8}
          style={{ marginBottom: 12 }}
          className="mc-estimated-program-status-mobile-selector"
        >
          <SelectComponent
            size="large"
            value={selectedMetric}
            onChange={setSelectedMetric}
            options={METRIC_OPTIONS}
            style={{ width: "100%" }}
            className="mc-estimated-program-status-metric-select"
          >
          </SelectComponent>
        </Flex>
      )}

      <Table
        columns={columns}
        rowKey={"unique_id"}
        scroll={tableSettings.scroll}
        showSizeChanger={false}
        withOutPagination={true}
        dataSource={dataSource}
        loading={mcEstimatedProgramStatusFetching}
        onChange={(_pagination, _filters, sorter) => setOrdering(toOrdering(sorter))}
        EmptyComponent={search.trim() || Object.keys(filterParams).length ? EpsNoResults : undefined}
        onRow={(record) => {
          const isExpandable = record?.months?.length > 0;
          return {
            onClick: () => rowClickHandler(record),
            className: cls({
              "expandable-row": isExpandable,
              expanded: expandedKeys.includes(record?.unique_id),
              "month-row": record?.row_type === "month",
              "month-row--last": record?.is_last_month,
              "month-row--future": record?.month_state === "future",
            }),
          };
        }}
        className={cls("mc-estimated-program-status-table", variant, { "is-mobile": isMobile })}
      />

      {withPagination && (
        <Pagination
          rootClassName={"ant-table-pagination by-reason-code-pagination"}
          onChange={(page) => setPageNumber(page)}
          total={count || 0}
          pageSize={pageSize}
          current={pageNumber}
          showLessItems={true}
          showTotal={(total, range) => (
            <Text>
              Showing{" "}
              <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text>{" "}
              results
            </Text>
          )}
          itemRender={(_, type, originalElement) =>
            pageSize >= count ? null : itemRender(_, type, originalElement, t)
          }
          showSizeChanger={false}
          showQuickJumper={false}
        />
      )}
    </div>
  );
};

export default MastercardEstimatedProgramStatusTable;
